// src/data/asterias.ts — Fase 4: 6 Asterias con roles, habilidades y set estratégico
import type { AsteriaSpecies } from '../entities/asteria.js';
import { MOVES } from './moves.js';

export const ASTERIAS: AsteriaSpecies[] = [
  {
    id: 'verdora',
    name: 'Verdora',
    element: 'terra',
    rarity: 'comun',
    habitat: ['hierba', 'bosque', 'claro'],
    behavior: 'tranquila',
    description: 'Pequeña brotadora de hojas cristalinas. Absorbe luz de la aurora y la devuelve como rocío.',
    lore: 'Donde Verdora duerme, la hierba crece en espiral. Los niños dejan semillas en el Árbol Madre para verla.',
    baseStats: { hp: 44, atk: 40, def: 48, spd: 36, energy: 14 },
    captureRate: 0.52,
    color: '#2e7d4a',
    accent: '#7cf0ff',
    silhouette: 'hoja',
    role: 'soporte',
    abilityId: 'fotosintesis',
    moves: [MOVES.brote, MOVES.latigo, MOVES.terreno_vegetal, MOVES.foco],
    evolution: { to:'sylvora', level:18, note:'Con vínculo alto evoluciona a Sylvora al atardecer.' }
  },
  {
    id: 'voltine',
    name: 'Voltine',
    element: 'astra',
    rarity: 'comun',
    habitat: ['hierba', 'ruina', 'claro'],
    behavior: 'curiosa',
    description: 'Chispa flotante con dos anillos opuestos. Parpadea al ritmo de tu respiración.',
    lore: 'No es electricidad: es “memoria del cielo”. Aparece donde cayó una estrella en la ruina.',
    baseStats: { hp: 36, atk: 48, def: 30, spd: 64, energy: 13 },
    captureRate: 0.48,
    color: '#ffd23a',
    accent: '#6a5cff',
    silhouette: 'chispa',
    role: 'rapida',
    abilityId: 'sobrecarga',
    moves: [MOVES.chispa, MOVES.destello, MOVES.tormenta, MOVES.foco],
    evolution: { to:'voltara', level:20, note:'Evoluciona si conoce Destello + vínculo >40.' }
  },
  {
    id: 'neritha',
    name: 'Neritha',
    element: 'aqua',
    rarity: 'poco_comun',
    habitat: ['agua', 'puente'],
    behavior: 'timida',
    description: 'Ajolotita translúcida de tres branquias que filtra el Río Zarco. Canta bajo el agua.',
    lore: 'Si te quedas inmóvil en el puente, asoma. Joren le deja migas. Solo se muestra a quien no la persigue.',
    baseStats: { hp: 42, atk: 38, def: 42, spd: 46, energy: 14 },
    captureRate: 0.42,
    color: '#3aa0ff',
    accent: '#b7ffdb',
    silhouette: 'ajolote',
    role: 'equilibrada',
    abilityId: 'velo_fluido',
    moves: [MOVES.burbuja, MOVES.onda, MOVES.lluvia, MOVES.velo_aqua],
    evolution: { to:'nerithra', level:22, note:'Evoluciona cerca del agua de noche.' }
  },
  {
    id: 'pyrraxis',
    name: 'Pyrraxis',
    element: 'ignis',
    rarity: 'poco_comun',
    habitat: ['roca', 'hierba'],
    behavior: 'territorial',
    description: 'Pequeño draco de ceniza con brasas en el lomo. Le gusta tomar el sol sobre rocas.',
    lore: 'Defiende su roca como tesoro. Si le ofreces respeto, te deja acercarte. Su ceniza fertiliza la Ruta.',
    baseStats: { hp: 40, atk: 55, def: 34, spd: 50, energy: 13 },
    captureRate: 0.38,
    color: '#ff6b3a',
    accent: '#ffcf6b',
    silhouette: 'draco',
    role: 'atacante',
    abilityId: 'brasa_persistente',
    moves: [MOVES.brasa, MOVES.rugido, MOVES.terreno_rocoso, MOVES.foco],
    evolution: { to:'pyrragon', level:24, note:'Evoluciona tras ganar 5 combates.' }
  },
  {
    id: 'umbrelle',
    name: 'Umbrelle',
    element: 'umbra',
    rarity: 'rara',
    habitat: ['cueva', 'claro', 'bosque'],
    behavior: 'nocturna',
    description: 'Polilla sombría de alas translúcidas que absorben luz. Solo abre ojos de noche.',
    lore: 'Vive en la Cueva Susurrante y el claro escondido. De día invisible; de noche flota como tinta. Lya guarda su secreto.',
    baseStats: { hp: 38, atk: 42, def: 36, spd: 60, energy: 13 },
    captureRate: 0.32,
    color: '#2a2f4a',
    accent: '#ff6b9d',
    silhouette: 'polilla',
    role: 'control',
    abilityId: 'manto_nocturno',
    moves: [MOVES.sombra, MOVES.susurro, MOVES.niebla, MOVES.interferencia],
    evolution: { to:'umbrellis', level:26, note:'Evoluciona solo de noche con vínculo >50.' }
  },
  {
    id: 'geodra',
    name: 'Geodra',
    element: 'terra',
    rarity: 'rara',
    habitat: ['roca', 'ruina', 'cueva'],
    behavior: 'estable',
    description: 'Pequeño gólem de geoda. Su interior brilla con cristales que vibran con la Ruina.',
    lore: 'Se forma donde la roca escuchó demasiado la aurora. Lento, inquebrantable. Ori dice que cada Geodra es un mapa.',
    baseStats: { hp: 52, atk: 44, def: 62, spd: 26, energy: 15 },
    captureRate: 0.30,
    color: '#8a7a9a',
    accent: '#7cf0ff',
    silhouette: 'geoda',
    role: 'defensiva',
    abilityId: 'nucleo_estable',
    moves: [MOVES.pedrada, MOVES.endurecer, MOVES.terreno_rocoso, MOVES.velo_aqua],
    evolution: { to:'geodrax', level:28, note:'Evoluciona tras comer 3 Cristales Lumen.' }
  },
];

export const ASTERIA_BY_ID = Object.fromEntries(ASTERIAS.map(a=>[a.id,a])) as Record<string, AsteriaSpecies>;

export function getSpecies(id:string): AsteriaSpecies | undefined { return ASTERIA_BY_ID[id]; }
